import { FunctionComponent, useContext } from 'react'
import styled, { css } from 'styled-components'
import { ellipsis } from 'polished'
import contextSportsHeader, {
  SportsHeaderContextType,
} from '@contexts/sportsHeader'
import { HorizontalRankingItemProps } from '@widgets/Teaser/sportsHeader/types'
import { computeTeaserWidth } from '@widgets/Teaser/utils'
import { ViewportTypeValues } from '@contexts/viewport'
import { desktopCSS, tabletCSS } from '@measures/responsive'

type StyledHorizontalRankingItemProps = Pick<
  SportsHeaderContextType,
  'teaserFlex'
>
type StyledItemPositionProps = Pick<SportsHeaderContextType, 'teaserFlex'>
type StyledItemNameProps = Pick<SportsHeaderContextType, 'teaserFlex'>
type StyledItemTimeProps = Pick<SportsHeaderContextType, 'teaserFlex'>

const { SportsHeaderContext } = contextSportsHeader

const StyledHorizontalRankingItem = styled.div<StyledHorizontalRankingItemProps>`
  display: grid;
  grid-template-areas:
    'position name'
    'position time';
  align-items: center;
  ${({ teaserFlex }) => {
    let gridColumnGapDesktop
    const desktopTeaserFlex = teaserFlex['desktop']
    const teaserWidthDesktop = computeTeaserWidth({
      flexValue: desktopTeaserFlex,
      viewportType: ViewportTypeValues.DESKTOP,
    })

    let parentGridPaddingLeftDesktop = 0
    let parentGridColumnGapDesktop = 0

    if (desktopTeaserFlex === '6') {
      parentGridPaddingLeftDesktop = 20
      parentGridColumnGapDesktop = 40
      gridColumnGapDesktop = '20px'
    } else if (desktopTeaserFlex === '4') {
      parentGridPaddingLeftDesktop = 20
      parentGridColumnGapDesktop = 40
      gridColumnGapDesktop = '20px'
    } else if (desktopTeaserFlex === '3') {
      parentGridPaddingLeftDesktop = 10
      parentGridColumnGapDesktop = 20
      gridColumnGapDesktop = '10px'
    }

    const maxWidthDesktop = `${Math.floor(
      (teaserWidthDesktop -
        parentGridPaddingLeftDesktop -
        parentGridPaddingLeftDesktop / 2 -
        2 * parentGridColumnGapDesktop) /
        3
    )}px`

    let gridColumnGapTablet
    const tabletTeaserFlex = teaserFlex['tablet']
    const teaserWidthTablet = computeTeaserWidth({
      flexValue: tabletTeaserFlex,
      viewportType: ViewportTypeValues.TABLET,
    })

    let parentGridPaddingLeftTablet = 0
    let parentGridColumnGapTablet = 0

    if (tabletTeaserFlex === '6') {
      parentGridPaddingLeftTablet = 20
      parentGridColumnGapTablet = 40
      gridColumnGapTablet = '20px'
    } else if (tabletTeaserFlex === '3') {
      parentGridPaddingLeftTablet = 10
      parentGridColumnGapTablet = 20
      gridColumnGapTablet = '10px'
    }

    const maxWidthTablet = `${Math.floor(
      (teaserWidthTablet -
        parentGridPaddingLeftTablet -
        parentGridPaddingLeftTablet / 2 -
        2 * parentGridColumnGapTablet) /
        3
    )}px`

    return css`
      ${desktopCSS(css`
        grid-column-gap: ${gridColumnGapDesktop};
        max-width: ${maxWidthDesktop};
      `)}

      ${tabletCSS(css`
        grid-column-gap: ${gridColumnGapTablet};
        max-width: ${maxWidthTablet};
      `)}
    `
  }}
`

const StyledItemPosition = styled.div<StyledItemPositionProps>`
  grid-area: position;
  ${({
    theme: {
      colors: { white },
      fonts: { goodOT: goodOTFontName },
    },
    teaserFlex,
  }) => {
    let fontSizeDesktop
    const desktopTeaserFlex = teaserFlex['desktop']
    const teaserWidthDesktop = computeTeaserWidth({
      flexValue: desktopTeaserFlex,
      viewportType: ViewportTypeValues.DESKTOP,
    })

    if (desktopTeaserFlex === '6') {
      fontSizeDesktop = `${0.04365 * teaserWidthDesktop}px`
    } else if (desktopTeaserFlex === '4') {
      fontSizeDesktop = `${0.06626 * teaserWidthDesktop}px`
    } else if (desktopTeaserFlex === '3') {
      fontSizeDesktop = `${0.0752 * teaserWidthDesktop}px`
    }

    let fontSizeTablet
    const tabletTeaserFlex = teaserFlex['tablet']
    const teaserWidthTablet = computeTeaserWidth({
      flexValue: tabletTeaserFlex,
      viewportType: ViewportTypeValues.TABLET,
    })

    if (tabletTeaserFlex === '6') {
      fontSizeTablet = `${0.06626 * teaserWidthTablet}px`
    } else if (tabletTeaserFlex === '3') {
      fontSizeTablet = `${0.0752 * teaserWidthTablet}px`
    }

    return css`
      color: ${white};
      font-family: ${goodOTFontName}, 'Arial Narrow', Impact;
      line-height: 1;
      font-weight: 900;
      ${desktopCSS(css`
        font-size: ${fontSizeDesktop};
      `)}

      ${tabletCSS(css`
        font-size: ${fontSizeTablet};
      `)}
    `
  }}
`

const StyledItemName = styled.div<StyledItemNameProps>`
  grid-area: name;
  ${ellipsis('100%', 1)};
  ${({
    theme: {
      colors: { white },
      fonts: { goodOT: goodOTFontName },
    },
    teaserFlex,
  }) => {
    let fontSizeDesktop
    const desktopTeaserFlex = teaserFlex['desktop']
    const teaserWidthDesktop = computeTeaserWidth({
      flexValue: desktopTeaserFlex,
      viewportType: ViewportTypeValues.DESKTOP,
    })

    if (desktopTeaserFlex === '6') {
      fontSizeDesktop = `${0.01686 * teaserWidthDesktop}px`
    } else if (desktopTeaserFlex === '4') {
      fontSizeDesktop = `${0.0256 * teaserWidthDesktop}px`
    } else if (desktopTeaserFlex === '3') {
      fontSizeDesktop = `${0.02845 * teaserWidthDesktop}px`
    }

    let fontSizeTablet
    const tabletTeaserFlex = teaserFlex['tablet']
    const teaserWidthTablet = computeTeaserWidth({
      flexValue: tabletTeaserFlex,
      viewportType: ViewportTypeValues.TABLET,
    })

    if (tabletTeaserFlex === '6') {
      fontSizeTablet = `${0.0256 * teaserWidthTablet}px`
    } else if (tabletTeaserFlex === '3') {
      fontSizeTablet = `${0.02845 * teaserWidthTablet}px`
    }

    return css`
      color: ${white};
      font-family: ${goodOTFontName}, 'Arial Narrow', Impact;
      line-height: 1;
      font-weight: 900;
      ${desktopCSS(css`
        font-size: ${fontSizeDesktop};
      `)}

      ${tabletCSS(css`
        font-size: ${fontSizeTablet};
      `)}
    `
  }}
`

const StyledItemTime = styled.div<StyledItemTimeProps>`
  grid-area: time;
  ${({
    theme: {
      colors: { white },
      fonts: { goodOT: goodOTFontName },
    },
    teaserFlex,
  }) => {
    let fontSizeDesktop
    const desktopTeaserFlex = teaserFlex['desktop']
    const teaserWidthDesktop = computeTeaserWidth({
      flexValue: desktopTeaserFlex,
      viewportType: ViewportTypeValues.DESKTOP,
    })

    if (desktopTeaserFlex === '6') {
      fontSizeDesktop = `${0.01686 * teaserWidthDesktop}px`
    } else if (desktopTeaserFlex === '4') {
      fontSizeDesktop = `${0.0256 * teaserWidthDesktop}px`
    } else if (desktopTeaserFlex === '3') {
      fontSizeDesktop = `${0.02845 * teaserWidthDesktop}px`
    }

    let fontSizeTablet
    const tabletTeaserFlex = teaserFlex['tablet']
    const teaserWidthTablet = computeTeaserWidth({
      flexValue: tabletTeaserFlex,
      viewportType: ViewportTypeValues.TABLET,
    })

    if (tabletTeaserFlex === '6') {
      fontSizeTablet = `${0.0256 * teaserWidthTablet}px`
    } else if (tabletTeaserFlex === '3') {
      fontSizeTablet = `${0.02845 * teaserWidthTablet}px`
    }

    return css`
      color: ${white};
      font-family: ${goodOTFontName}, 'Arial Narrow', Impact;
      line-height: 1;
      font-weight: 500;
      ${desktopCSS(css`
        font-size: ${fontSizeDesktop};
      `)}

      ${tabletCSS(css`
        font-size: ${fontSizeTablet};
      `)}
    `
  }}
`

const HorizontalRankingItem: FunctionComponent<HorizontalRankingItemProps> = ({
  competitor: { position, name, time },
}) => {
  const { teaserFlex } = useContext(SportsHeaderContext)

  return (
    <StyledHorizontalRankingItem teaserFlex={teaserFlex}>
      <StyledItemPosition teaserFlex={teaserFlex}>
        {position}.
      </StyledItemPosition>
      <StyledItemName teaserFlex={teaserFlex}>{name}</StyledItemName>
      <StyledItemTime teaserFlex={teaserFlex}>{time}</StyledItemTime>
    </StyledHorizontalRankingItem>
  )
}

export default HorizontalRankingItem
